import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';

import {
  fetchCampaignBudgets,
  setCampaignBudget,
} from '@src/api/actions/dashboard/facebookZipTargeting';
import { useMessage } from '@src/context/common';

const CHANNEL_LABELS = { facebook: 'Facebook', google: 'Google', bing: 'Bing' };

const money = (value) =>
  value == null
    ? '—'
    : `$${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const rowKey = (row) => `${row.channel}:${row.campaign_id}`;

/**
 * Live daily budgets for the client's running Facebook, Google and Bing
 * campaigns, editable one at a time. A Facebook edit is one-off — the 7am
 * optimizer sets it again tomorrow. Shared budgets and ad-set budgets are
 * read-only here.
 */
export default function CampaignBudgetsCard({ clientId, header }) {
  const message = useMessage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState('');

  const load = useCallback(() => {
    if (!clientId) {
      setData(null);
      return;
    }
    setLoading(true);
    setError('');
    fetchCampaignBudgets({ clientId })
      .then((response) => {
        setData(response.data || { rows: [], errors: {}, edits: [] });
        setDrafts({});
      })
      .catch((err) => setError(err?.message || 'Failed to load budgets.'))
      .finally(() => setLoading(false));
  }, [clientId]);

  useEffect(() => {
    load();
  }, [load]);

  const save = (row) => {
    const value = drafts[rowKey(row)];
    setSaving(rowKey(row));
    setCampaignBudget({ clientId, channel: row.channel, campaignId: row.campaign_id, dailyBudget: value })
      .then(() => {
        message.success(`${row.campaign_name}: ${money(row.daily_budget)} → ${money(value)}/day`);
        load();
      })
      .catch((err) => message.error(err?.message || 'Budget change failed.'))
      .finally(() => setSaving(''));
  };

  const rows = data?.rows || [];
  const channelErrors = Object.entries(data?.errors || {});
  const totals = rows.reduce((acc, row) => {
    acc[row.channel] = (acc[row.channel] || 0) + (row.daily_budget || 0);
    return acc;
  }, {});

  return (
    <Card>
      {header}
      <Divider />
      <CardContent>
        <Stack spacing={1.5}>
          {!clientId ? (
            <Typography color="text.secondary">Pick a client to see its campaign budgets.</Typography>
          ) : (
            <>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Typography variant="caption" color="text.secondary">
                  Daily budgets read live from each platform. Changes go live now. Facebook edits are
                  one-off: the 7am optimizer sets the budget again tomorrow.
                  {Object.keys(totals).length
                    ? ` Total/day: ${Object.entries(totals)
                        .map(([channel, total]) => `${CHANNEL_LABELS[channel]} ${money(total)}`)
                        .join(' · ')}.`
                    : ''}
                </Typography>
                <Button size="small" variant="text" onClick={load} disabled={loading}>
                  Refresh
                </Button>
              </Stack>
              {error && <Alert severity="error">{error}</Alert>}
              {channelErrors.map(([channel, text]) => (
                <Alert key={channel} severity="warning">
                  {CHANNEL_LABELS[channel] || channel} budgets couldn&apos;t be read: {text}
                </Alert>
              ))}
              {loading && !data ? (
                <CircularProgress size={20} />
              ) : rows.length ? (
                <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Channel</TableCell>
                        <TableCell>Campaign</TableCell>
                        <TableCell align="right">Daily budget</TableCell>
                        <TableCell sx={{ width: 220 }}>New daily budget</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((row) => {
                        const key = rowKey(row);
                        const draft = drafts[key] ?? '';
                        const changed = draft !== '' && Number(draft) > 0 && Number(draft) !== row.daily_budget;
                        return (
                          <TableRow key={key}>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              <Chip
                                label={[CHANNEL_LABELS[row.channel], row.kind].filter(Boolean).join(' ')}
                                size="small"
                                variant="outlined"
                                sx={{ height: 20 }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{row.campaign_name}</Typography>
                              {row.note && (
                                <Typography variant="caption" color="text.secondary">
                                  {row.note}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                              {money(row.daily_budget)}
                            </TableCell>
                            <TableCell>
                              {row.editable ? (
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={draft}
                                    placeholder={row.daily_budget != null ? String(row.daily_budget) : ''}
                                    onChange={(event) => setDrafts((prev) => ({ ...prev, [key]: event.target.value }))}
                                    onKeyDown={(event) => {
                                      if (event.key === 'Enter' && changed) save(row);
                                    }}
                                    inputProps={{ min: 1, step: 1 }}
                                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                    sx={{ width: 130 }}
                                    disabled={saving === key}
                                  />
                                  <Button
                                    size="small"
                                    variant="contained"
                                    onClick={() => save(row)}
                                    disabled={!changed || Boolean(saving)}
                                  >
                                    {saving === key ? 'Saving…' : 'Save'}
                                  </Button>
                                </Stack>
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  Read-only
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                !loading && <Typography color="text.secondary">No live campaigns found.</Typography>
              )}
              {data?.edits?.length ? (
                <Stack spacing={0.5}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Recent budget edits
                  </Typography>
                  {data.edits.map((edit) => (
                    <Typography
                      key={edit.id}
                      variant="caption"
                      color={edit.success ? 'text.secondary' : 'error.main'}
                    >
                      {new Date(edit.created_at).toLocaleString()} · {edit.channel} · {edit.campaign_name || edit.campaign_id}:{' '}
                      {money(edit.previous_daily_budget)} → {money(edit.new_daily_budget)}
                      {edit.actor_name ? ` · ${edit.actor_name}` : ''}
                      {edit.success ? '' : ` · FAILED: ${edit.error}`}
                    </Typography>
                  ))}
                </Stack>
              ) : null}
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
